import { z } from "zod";
import {
    type BoardLayoutCacheOf,
    boardLayoutSchema,
} from "@components/views/board/boardLayoutCache";

/** Strips fields older entries stored (`wasEmptyWhenSet`), as `z.object` drops unknown keys. */
const columnOverrideSchema = z.object({
    columnId: z.string(),
    isCollapsed: z.boolean(),
});

/**
 * A column the user collapsed by hand while it had cards. Older entries
 * with `isCollapsed: false` (saved expands) are ignored on read.
 */
export type TaskBoardColumnOverride = z.infer<typeof columnOverrideSchema>;

const viewCollapseSchema = z.object({
    columnOverrides: z.array(columnOverrideSchema).catch([]),
    collapsedLaneIds: z.array(z.string()).catch([]),
});

/** One view's collapse state: the column overrides and the collapsed lanes. */
export type TaskBoardViewCollapse = z.infer<typeof viewCollapseSchema>;

/** Collapse state by view id (`NO_VIEW_KEY` with no view picked). */
export type TaskBoardCollapseByView = Record<string, TaskBoardViewCollapse>;

/**
 * Writes the collapse state `update` makes from the latest written one, so
 * writes in the same tick (two failed saves, say) compose.
 */
export type UpdateTaskBoardCollapse = (
    update: (
        collapseByViewId: TaskBoardCollapseByView,
    ) => TaskBoardCollapseByView,
) => void;

/**
 * The Tasks page's stored layout and per-view collapse state. A stored
 * collapse that no longer parses (the old page-wide shape) resets to empty
 * alone, keeping the layout.
 */
export const taskBoardLayoutCacheSchema = z.object({
    layout: boardLayoutSchema,
    board: z.record(z.string(), viewCollapseSchema).catch({}),
});

export type TaskBoardLayoutCache = BoardLayoutCacheOf<TaskBoardCollapseByView>;

export const DEFAULT_TASK_BOARD_LAYOUT_CACHE: TaskBoardLayoutCache = {
    layout: "list",
    board: {},
};

export const EMPTY_VIEW_COLLAPSE: TaskBoardViewCollapse = {
    columnOverrides: [],
    collapsedLaneIds: [],
};

/** One view's column overrides in `collapseByViewId`. */
export const getViewColumnOverrides = ({
    collapseByViewId,
    viewKey,
}: {
    collapseByViewId: TaskBoardCollapseByView;
    viewKey: string;
}) => (collapseByViewId[viewKey] ?? EMPTY_VIEW_COLLAPSE).columnOverrides;

/** The collapse state with `patch` over one view's, keeping the rest of that view's. */
export const patchViewCollapse = ({
    collapseByViewId,
    viewKey,
    patch,
}: {
    collapseByViewId: TaskBoardCollapseByView;
    viewKey: string;
    patch: Partial<TaskBoardViewCollapse>;
}): TaskBoardCollapseByView => ({
    ...collapseByViewId,
    [viewKey]: {
        ...(collapseByViewId[viewKey] ?? EMPTY_VIEW_COLLAPSE),
        ...patch,
    },
});

/**
 * The collapse state update that runs `update` over one view's column
 * overrides; it keeps the same state when `update` keeps them.
 */
export const updateViewColumnOverrides =
    ({
        viewKey,
        update,
    }: {
        viewKey: string;
        update: (
            overrides: readonly TaskBoardColumnOverride[],
        ) => readonly TaskBoardColumnOverride[];
    }) =>
    (collapseByViewId: TaskBoardCollapseByView): TaskBoardCollapseByView => {
        const overrides = getViewColumnOverrides({ collapseByViewId, viewKey });
        const nextOverrides = update(overrides);
        return nextOverrides === overrides
            ? collapseByViewId
            : patchViewCollapse({
                  collapseByViewId,
                  viewKey,
                  patch: { columnOverrides: [...nextOverrides] },
              });
    };

/** The columns the overrides collapse by hand; legacy saved expands are left out. */
export const getHandCollapsedIds = (
    overrides: readonly TaskBoardColumnOverride[],
): string[] =>
    overrides
        .filter((override) => override.isCollapsed)
        .map((override) => override.columnId);

/** The overrides that store `handCollapsedIds`. */
export const toHandCollapsedOverrides = (
    handCollapsedIds: readonly string[],
): TaskBoardColumnOverride[] =>
    handCollapsedIds.map((columnId) => ({ columnId, isCollapsed: true }));

/**
 * The overrides left once cards land in `columnIds`, on any view: a drop
 * clears its columns' hand collapses. The same array when it cleared none.
 */
export const clearDroppedOverrides = ({
    overrides,
    columnIds,
}: {
    overrides: readonly TaskBoardColumnOverride[];
    columnIds: readonly string[];
}): readonly TaskBoardColumnOverride[] => {
    const isCleared = (override: TaskBoardColumnOverride) =>
        override.isCollapsed && columnIds.includes(override.columnId);
    return overrides.some(isCleared)
        ? overrides.filter((override) => !isCleared(override))
        : overrides;
};

/**
 * The overrides with those a drop cleared in `columnIds` put back, once its
 * cards went back (a failed save, a canceled confirmation). Callers leave out
 * the columns the user toggled since the drop; a column with an override
 * again keeps it. The same array when nothing comes back.
 */
export const restoreClearedOverrides = ({
    overrides,
    clearedOverrides,
    columnIds,
}: {
    overrides: readonly TaskBoardColumnOverride[];
    clearedOverrides: readonly TaskBoardColumnOverride[];
    columnIds: readonly string[];
}): readonly TaskBoardColumnOverride[] => {
    const restoredOverrides = clearedOverrides.filter(
        (cleared) =>
            columnIds.includes(cleared.columnId) &&
            !overrides.some(
                (override) => override.columnId === cleared.columnId,
            ),
    );
    return restoredOverrides.length === 0
        ? overrides
        : [...overrides, ...restoredOverrides];
};
